import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, Type, WritableSignal } from '@angular/core';
import { Params } from '@angular/router';
import { finalize, forkJoin, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { fromDateOnlyString, toDateOnlyString } from '../../core/date';
import { PermissionService } from '../../core/security';
import { GlobalConfigService } from '../../core/services';
// Deep import: the layout barrel pulls the sidebar, whose config imports reporting — barrel here closes a module cycle.
import { LayoutService } from '../../layout/layout.service';
import { HierarchyNodeLevelResponseDto, HierarchyNodeLevelService } from '../hierarchy';
import { ColumnQueryState, ReportColumnDefinition, ReportColumnGroupDefinition, ReportColumnService } from '../reporting/report-column';
import { ReportGridComponent } from '../reporting/report-grid/report-grid.component';
import { ReportParameterDefinition } from '../reporting/report-parameters';
import { ReportParametersService } from '../reporting/report-parameters/report-parameters.service';
import {
    ExportOptions,
    FilterSetDefinition,
    LayoutDefinition,
    ParameterSetDefinition,
    ReportCategory,
    ReportColumnMap,
    ReportDefinition,
    ReportInitializationOptions,
    ReportItemCreateRequestDto,
    ReportItemEntity,
    ReportItemScopeKey,
    ReportItemTypeKey,
    ReportOptions,
    ReportViewEntity,
    ReportViewUpsertRequestDto
} from '../reporting/reporting.model';
import { ReportService } from '../reporting/report.service';
import { REPORT_REGISTRY, ReportKey } from '../reporting/reports/report.registry';
import { SubscriberResponseDto, SubscriberService } from '../subscriber';
import { UserService } from '../user';
import { ReportItemService } from './reporting-page/report-drawer/saved-items/report-item.service';
import { ReportingSavedItemCategoryService } from './reporting-page/report-drawer/saved-items/reporting-saved-item-category.service';
import { ReportViewService } from './reporting-page/report-drawer/saved-items/saved-views/report-view.service';

const DEFAULT_REPORT_OPTIONS: Required<ReportOptions> = {
    showAdminTools: true,
    showSubscriberSelect: true,
    showIncludeAllColumnsToggle: true,
    showParameters: true,
    showSavedParameters: true,
    showSavedLayouts: true,
    showSavedFilters: true,
    showSavedViews: true,
    showReportTitle: true,
    showExpandAll: true,
    showCollapseAll: true,
    showAutosizeColumns: true,
    showClearFilters: true,
    showSearch: true,
    exportOptions: ['CSV', 'EXCEL']
};

@Injectable()
export class ReportWorkspaceService {
    private httpClient = inject(HttpClient);
    private reportService = inject(ReportService);
    private globalConfigService = inject(GlobalConfigService);

    private columnService = inject(ReportColumnService);
    private hierarchyNodeLevelService = inject(HierarchyNodeLevelService);
    private layoutService = inject(LayoutService);
    private parametersService = inject(ReportParametersService);
    private permissionService = inject(PermissionService);
    private reportItemService = inject(ReportItemService);
    private reportViewService = inject(ReportViewService);
    private savedItemCategoryService = inject(ReportingSavedItemCategoryService);
    private subscriberService = inject(SubscriberService);
    private userService = inject(UserService);

    // #region initialization
    /********************************************/
    /************** Initialization **************/
    /********************************************/
    /**
     * Sets all inputs required to initialize a report instance.
     * This method is the single entry point for configuring the workspace state for a report.
     */
    setReportContext(reportKey: ReportKey, queryParams: Params): Observable<void> {
        this._isReportReady.set(false);

        /* Report Key */
        this._reportKey.set(reportKey);

        /* Query Params */
        this._queryParams.set(queryParams);

        /* Report Query Options */
        const initializationOptions = this.getInitializationOptions(queryParams);
        this._initializationOptions.set(initializationOptions);

        /* Report Definition */
        const reportDefinition = {
            dataComponent: ReportGridComponent, // Use the Grid Component by default
            ...REPORT_REGISTRY[reportKey]
        };

        if (!reportDefinition) {
            throw new Error(`Unknown report: ${reportKey}`);
        }

        this._reportDefinition.set(reportDefinition);
        this._reportName.set(null);

        /* Subscriber */
        const subscriberKey = this.getDefaultSubscriberKey(queryParams, reportDefinition);
        this._defaultSubscriberKey.set(subscriberKey);
        this._subscriberKey.set(subscriberKey);

        /* Fetch report name from the API */
        this.reportService.getByKey(reportDefinition.reportKey).subscribe((dto) => this._reportName.set(dto.reportName));

        /* Initialize Parameters */
        this.initializeParameters(reportDefinition, queryParams);

        /* Initialize Subscriber */
        const subscriber$ = subscriberKey == null ? of(null) : this.subscriberService.getByKey(subscriberKey).pipe(tap((subscriber) => this._subscriber.set(subscriber)));

        /* Initialize Columns */
        const initializeColumns$ = this.initializeColumns(reportDefinition, queryParams);

        /* Initialize Layouts */
        const initializeLayouts$ = this.initializeSavedItems(subscriberKey);

        /* Initial Actions */
        if (initializationOptions.autoRun) {
            this.autoRun();
        }

        /** Phase 1
         *  - Load Layout and Layout Categories from the Server.
         */
        const phase1$ = forkJoin([subscriber$, initializeLayouts$]);

        /** Phase 2
         *  - Initialize Columns.
         *      - Layouts must be set before the active column definition can be computed.
         *      - Pulls column maps from the Server.
         */
        const phase2$ = phase1$.pipe(switchMap(() => initializeColumns$));

        return phase2$.pipe(
            tap(() => {
                /* Initialize State - Requires the Layouts to be loaded. */
                const reportColumnDefinition = reportDefinition.reportColumnDefinition;
                if (reportColumnDefinition) {
                    this.setActiveLayout(initializationOptions.activeLayoutKey ?? null);
                }

                this._isReportReady.set(true);
            }),
            map(() => undefined)
        );
    }

    /**
     * Resolves the effective SubscriberKey for the current report context.
     *
     * For sysadmin:
     *  - Uses `p_subscriberKey` from query parameters if present and valid.
     *  - Falls back to the Global Subscriber
     *
     * For subscriber users:
     *  - Uses the subscriber assigned to the user context.
     *
     * @param queryParams The current route query parameters
     * @returns The resolved SubscriberKey or null if unavailable
     */
    private getDefaultSubscriberKey(queryParams: Params, reportDefinition: ReportDefinition): number | null {
        let userSubscriberKey: number | null = null;
        const userContext = this.userService.userContext();

        if (reportDefinition.subscriberKeySource === 'currentUser') {
            userSubscriberKey = userContext?.subscriber.subscriberKey ?? null;

            if (userSubscriberKey === null) {
                throw new Error(`Report Parameters: User's Subscriber is unknown.`);
            }

            return userSubscriberKey;
        }

        if (this.permissionService.hasPermission('sysAdmin')) {
            const querySubscriberKey = queryParams['p_subscriberKey'];
            if (querySubscriberKey != null) {
                const parsed = Number(this.parametersService.parseParameterValue('number', querySubscriberKey));

                if (parsed != null) {
                    return parsed as number;
                }
            }

            return this.layoutService.activeSubscriberKey();
        } else {
            userSubscriberKey = userContext?.subscriber.subscriberKey ?? null;

            if (userSubscriberKey === null) {
                throw new Error(`Report Parameters: User's Subscriber is unknown.`);
            }
        }

        return userSubscriberKey;
    }

    private getInitializationOptions(queryParams: Params): ReportInitializationOptions {
        const autoRun = queryParams['o_autoRun'] === 'true';
        const queryActiveLayoutKey = queryParams['o_activeLayoutKey'];
        const activeLayoutKey = queryActiveLayoutKey == null ? null : Number(queryActiveLayoutKey);
        const enableDevTools = queryParams['o_enableDevTools'] === 'true' || queryParams['dev'] === 'true';
        const showDevTools = queryParams['o_showDevTools'] === 'true';

        const initializationOptions: ReportInitializationOptions = {
            autoRun: autoRun,
            activeLayoutKey: Number.isFinite(activeLayoutKey) ? activeLayoutKey : null,
            enableDevTools: enableDevTools,
            showDevTools: showDevTools
        };

        return initializationOptions;
    }

    /** Initialized all parameter definitions and their initial values for the active report context. */
    private initializeParameters(reportDefinition: ReportDefinition, queryParams: Params) {
        /* Get Parameter Definitions */
        const parameterDefinitions = this.parametersService.getParameterDefinitions(reportDefinition);
        this._parameterDefinitions.set(parameterDefinitions);

        /* Get Default Values from the Query */
        const queryParameterDefaults = this.parametersService.getParameterDefaultsFromQueryParams(queryParams);
        this._queryParameterDefaults.set(queryParameterDefaults);

        /* Default Parameter Values: Default Values, Parameter Values Signal, and Signal Map */
        this.parameterValueSignals = {};
        const initialValues: Record<string, unknown> = {};

        const allDefs = parameterDefinitions.flatMap((d) => [d, ...(d.additionalParameters ?? [])]);

        allDefs.forEach((def) => {
            const rawQueryValue = queryParameterDefaults[def.key] ?? null;
            const initialValue = def.key === 'subscriberKey' ? this._defaultSubscriberKey() : this.parametersService.resolveInitialParameterValue(def, rawQueryValue);

            this._defaultParameterValues[def.key] = initialValue;
            initialValues[def.key] = initialValue;
            this.parameterValueSignals[def.key] = signal(initialValue);
        });

        this.parameterValues.set(initialValues);
    }

    /** Initialized all column definitions and their initial values for the active report context. */
    private initializeColumns(reportDefinition: ReportDefinition, queryParams: Params): Observable<any> {
        const reportKey = reportDefinition.reportKey;

        /* Get Column State from Query */
        const queryColumnDefaults = this.columnService.getColumnDefaultsFromQueryParams(queryParams);
        this._queryColumnDefaults.set(queryColumnDefaults);

        /* Get Column Groups */
        const columnGroupDefs = this.columnService.getColumnGroupDefinitions(reportDefinition);
        this._columnGroupDefinitions.set(columnGroupDefs);

        /* Get Column Map */
        const columnMap$ = this.columnService.getColumnMap(reportKey);

        /* Get Hierarchy Levels */
        const subscriberKey = this.subscriberKey();
        const hierarchyNodeLevel$ = subscriberKey == null ? of([]) : this.hierarchyNodeLevelService.list(subscriberKey);

        return forkJoin({
            columnMap: columnMap$,
            hierarchyNodeLevels: hierarchyNodeLevel$
        }).pipe(
            tap(({ columnMap, hierarchyNodeLevels }) => {
                this._columnMap.set(columnMap);
                this._hierarchyNodeLevels.set(hierarchyNodeLevels);

                this.recomputeColumnDefinitions();
            })
        );
    }

    private initializeSavedItems(subscriberKey: number | null): Observable<any> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        if (subscriberKey == null && this.reportDefinition()?.subscriberKeySource !== 'currentUser') {
            this.setCategories([]);
            this.setReportItems([]);
            this._reportViews.set([]);
            return of(null);
        }

        const categories$ = this.savedItemCategoryService.get(reportKey, subscriberKey);
        const reportItems$ = this.reportItemService.get(reportKey, subscriberKey);

        const reportViews$ = this.reportViewService.get(reportKey, subscriberKey);

        return forkJoin([categories$, reportItems$, reportViews$]).pipe(
            tap(([categories, reportItems, reportViews]) => {
                this.setCategories(categories);
                this.setReportItems(reportItems);
                this._reportViews.set(reportViews);
            })
        );
    }

    private setReportItems(reportItems: ReportItemEntity[]) {
        this._reportItems.set(reportItems);
        this._layouts.set(reportItems.filter((item) => item.reportItemTypeKey === ReportItemTypeKey.Layout) as ReportItemEntity<LayoutDefinition>[]);
        this._parameterSets.set(reportItems.filter((item) => item.reportItemTypeKey === ReportItemTypeKey.Parameter) as ReportItemEntity<ParameterSetDefinition>[]);
        this._filterSets.set(reportItems.filter((item) => item.reportItemTypeKey === ReportItemTypeKey.Filter) as ReportItemEntity<FilterSetDefinition>[]);
    }

    private createLocalReportItem<TDefinition>(reportKey: ReportKey, itemKey: number, item: ReportItemCreateRequestDto<TDefinition>): ReportItemEntity<TDefinition> {
        return {
            itemKey,
            reportKey,
            ...item,
            rowStatusKey: 1,
            insertUser: '',
            insertDate: new Date(),
            updateUser: '',
            updateDate: ''
        };
    }

    private reloadCategories(): Observable<void> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(undefined);
        }

        return this.savedItemCategoryService.get(reportKey, this.subscriberKey()).pipe(
            tap((categories) => this.setCategories(categories)),
            map(() => undefined)
        );
    }

    private setCategories(categories: ReportCategory[]) {
        this._layoutCategories.set(this.getCategoriesForType(categories, ReportItemTypeKey.Layout));
        this._parameterSetCategories.set(this.getCategoriesForType(categories, ReportItemTypeKey.Parameter));
        this._filterSetCategories.set(this.getCategoriesForType(categories, ReportItemTypeKey.Filter));
        this._reportViewCategories.set(this.getCategoriesForType(categories, ReportItemTypeKey.View));
    }

    private getCategoriesForType(categories: ReportCategory[], reportItemTypeKey: ReportItemTypeKey): ReportCategory[] {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            throw new Error('Report is not initialized.');
        }

        return [
            ...categories.filter((category) => category.reportItemTypeKey === reportItemTypeKey),
            this.savedItemCategoryService.createUncategorizedCategory(reportKey, reportItemTypeKey)
        ];
    }

    // #endregion initialization

    // #region report definition
    /*************************************/
    /************** Report Definition **************/
    /*************************************/
    private _reportKey = signal<ReportKey | null>(null);
    /** The active report key for the current workspace context. */
    readonly reportKey = this._reportKey.asReadonly();

    private _reportDefinition = signal<ReportDefinition | null>(null);
    /** The active report definition for the current workspace context. */
    readonly reportDefinition = this._reportDefinition.asReadonly();

    private _reportName = signal<string | null>(null);
    /** The display name for the active report, loaded from the API. Null until the API response arrives. */
    readonly reportName = this._reportName.asReadonly();

    /** Resolved report options for the current report, with all defaults applied.
     * Merges `DEFAULT_REPORT_OPTIONS` with any overrides defined in the report definition.
     */
    readonly reportOptions = computed<Required<ReportOptions>>(() => ({
        ...DEFAULT_REPORT_OPTIONS,
        ...this.reportDefinition()?.reportOptions
    }));

    private _isReportReady = signal<boolean>(false);
    /** Indicates the report definition, parameters, and column configurations have been initialized. Report content and child components should not render until this is true.*/
    readonly isReportReady = this._isReportReady.asReadonly();

    /** Component responsible for rendering the report's row data. Defaults to the standard GridComponent if not provided. */
    readonly dataComponent = computed<Type<any>>(() => this.reportDefinition()?.dataComponent ?? ReportGridComponent);

    private _initializationOptions = signal<ReportInitializationOptions>({});
    /** Report initialization options. Controls startup behavior such as auto-run, active layout selection, and developer tools visibility.
     *
     * These are currently derived from query parameters (prefixed with `o_`).
     */
    readonly initializationOptions = this._initializationOptions.asReadonly();

    private _queryParams = signal<Params | null>(null);
    /** Raw route query parameters for the current report instance. */
    readonly queryParams = this._queryParams.asReadonly();

    // #endregion report definition

    // #region subscriber
    /*************************************/
    /************ Subscriber *************/
    /*************************************/
    private _defaultSubscriberKey = signal<number | null>(null);
    /**
     * The default SubscriberKey for the current report context.
     * Resolved once during initialization and does not change during the report lifecycle.
     *
     * For sysadmins: reflects the selected Global Subscriber. (or query param override).
     * For subscriber users: the subscriber assigned to the user's context.
     */
    readonly defaultSubscriberKey = this._defaultSubscriberKey.asReadonly();

    private _subscriberKey = signal<number | null>(null);
    /** The currently active SubscriberKey.
     * May differ from defaultSubscriberKey if the user has switched subscribers mid-session.
     */
    readonly subscriberKey = this._subscriberKey.asReadonly();

    readonly areSavedItemControlsDisabled = computed<boolean>(() => this.reportDefinition()?.subscriberKeySource !== 'currentUser' && this.subscriberKey() == null);

    /** Updates the active subscriber and triggers downstream side effects:
     * - Syncs the subscriberKey parameter value.
     * - Marks column definitions as stale if the report is already initialized.
     * - Refreshes hierarchy node levels for the new subscriber.
     */
    setSubscriberKey(subscriberKey: number | null) {
        this._subscriberKey.set(subscriberKey);
        this.setParam('subscriberKey', subscriberKey);

        if (this._isReportReady()) {
            this._areColumnDefinitionsStale.set(true);

            /* Update the Subscriber if the key changes after the report has been initialized */
            if (subscriberKey == null) {
                this._subscriber.set(null);
            } else {
                this.setSubscriber(subscriberKey);
            }
        }

        this.updateHierarchyNodeLevels(subscriberKey).subscribe();

        /* Update Saved Items */
        this._activeParameterSetKey.set(null);
        this._activeFilterSetKey.set(null);
        this._activeReportViewKey.set(null);
        this.initializeSavedItems(subscriberKey).subscribe();
    }

    private _subscriber = signal<SubscriberResponseDto | null>(null);
    /** Full subscriber object for the currently active subscriber. Populated when the subscriber is selected. */
    readonly subscriber = this._subscriber.asReadonly();

    /** Updates the active subscriber object. */
    private setSubscriber(subscriberKey: number) {
        this.subscriberService.getByKey(subscriberKey).subscribe((subscriber) => this._subscriber.set(subscriber));
    }

    // #endregion subscriber

    // #region user / security
    /******************************************/
    /************ User / Security *************/
    /******************************************/
    private _user = signal(this.userService.userContext());
    readonly user = this._user.asReadonly();
    readonly userId = computed(() => {
        const user = this.user();
        if (!user) {
            throw Error('User not found');
        }

        return user.id;
    });

    private _isSysAdmin = signal(this.permissionService.hasPermission('sysAdmin'));
    readonly isSysAdmin = this._isSysAdmin.asReadonly();

    setIsSysAdmin(isSysAdmin: boolean) {
        if (!this.permissionService.hasPermission('sysAdmin')) {
            return;
        }

        this._isSysAdmin.set(isSysAdmin);
    }

    private _isAdmin = signal(this.permissionService.hasPermission('admin'));
    readonly isAdmin = this._isAdmin.asReadonly();
    setIsAdmin(isAdmin: boolean) {
        if (!this.permissionService.hasPermission('sysAdmin')) {
            return;
        }

        this._isAdmin.set(isAdmin);
    }
    /** Indicates whether the current user has system administrator permissions. */

    // #endregion user / security

    // #region parameters
    /*************************************/
    /************ Parameters *************/
    /*************************************/
    private _parameterDefinitions = signal<ReportParameterDefinition[]>([]);
    /** All parameter definitions associated with the current report definition. */
    readonly parameterDefinitions = this._parameterDefinitions.asReadonly();

    private _areParametersReady = signal<boolean>(false);
    /** Indicates the Parameters component has initialized. Does not reflect individual parameter async loading. */
    readonly areParametersReady = this._areParametersReady.asReadonly();
    /** Updates the readiness of the Parameters component. */
    setParametersReady(areParametersReady: boolean) {
        this._areParametersReady.set(areParametersReady);
    }

    private _parametersLoading = signal<Record<string, boolean>>({});
    /** Tracks loading state per parameter key. True indicates the parameter is currently loading. */
    readonly parametersLoading = this._parametersLoading.asReadonly();
    /** Returns a computed signal indicating whether any parameter is currently in a loading state. */
    readonly areAnyParamsLoading = computed<boolean>(() => Object.values(this.parametersLoading()).some((v) => v));

    /** Updates the loading state for a specific parameter key. */
    setParamLoading(key: string, isLoading: boolean) {
        this._parametersLoading.update((p) => ({ ...p, [key]: isLoading }));
    }

    private _areParametersValid = signal<boolean>(false);
    /**Indicates whether the parameters form is currently valid. */
    readonly areParametersValid = this._areParametersValid.asReadonly();
    /** Updates the current parameters validation state. */
    setParametersValid(areParametersValid: boolean) {
        this._areParametersValid.set(areParametersValid);
    }

    private _queryParameterDefaults = signal<Record<string, string | string[]> | null>(null);
    /** Parameter default values parsed from query parameters (`p_` prefix). */
    readonly queryParameterDefaults = this._queryParameterDefaults.asReadonly();

    private _defaultParameterValues: Record<string, unknown> = {};
    /** Resolved default value for each parameter key after report initialization. */
    get defaultParameterValues() {
        return this._defaultParameterValues;
    }

    /** Aggregate store of all parameter values. Used when reacting to any parameter change. */
    parameterValues = signal<Record<string, any>>({});
    /** Fine grained parameter signals keyed by parameter key. Used for targeted reactivity per parameter. */
    private parameterValueSignals: Record<string, WritableSignal<any>> = {};

    /** Return the internal writable signal for a parameter key. Lazily creates the signal if it does not exist. */
    private getParamWriteableSignal<T>(key: string) {
        return this.parameterValueSignals[key];
    }

    /** Returns a readonly signal for the given parameter key.  */
    getParamSignal<T>(key: string) {
        return this.getParamWriteableSignal<T>(key)?.asReadonly();
    }

    /** Updates a parameter value in both the aggregate store and its dedicated signal. */
    setParam(key: string, value: any) {
        this.parameterValues.update((p) => ({ ...p, [key]: value }));

        const signal = this.getParamWriteableSignal(key);

        signal?.set(value);
    }

    private _isResettingParameters = signal(false);
    /** Indicates whether the parameters form is currently resetting. Used to suppress side effects during reset. */
    isResettingParameters = this._isResettingParameters.asReadonly();

    /**
     * Resets all parameter values to their configured emptyValue.
     *
     * Includes primary parameters and any additionalParameters defined on each parameter definition.
     *
     * Sets a temporary `isResettingParameters` flag to prevent parameter components from marking themselves as touched during reset-driven updates.
     */
    resetParameters() {
        this._isResettingParameters.set(true);
        this.setActiveParameterSet(null);

        const allDefs = this.parameterDefinitions().flatMap((d) => [d, ...(d.additionalParameters ?? [])]);

        for (const def of allDefs) {
            if (def.key === 'subscriberKey') {
                // Special Logic for SubscriberKey
                this.setSubscriberKey(this._defaultSubscriberKey());
                continue;
            }

            this.setParam(def.key, this._defaultParameterValues[def.key] ?? def.emptyValue);
        }

        this._parametersResetRequest.update((n) => n + 1);

        /* Delay clearing the reset flag to allow all parameter components to process their reset-triggered form events first. */
        setTimeout(() => this._isResettingParameters.set(false), 1);
    }

    // #endregion parameters

    // #region columns
    /*************************************/
    /************** Columns **************/
    /*************************************/
    private _includeAllColumns = signal<boolean>(false);
    /** Special Option for SysAdmins to always see all columns regardless of subscriber. */
    readonly includeAllColumns = this._includeAllColumns.asReadonly();
    setIncludeAllColumns(includeAllColumns: boolean) {
        this._includeAllColumns.set(includeAllColumns);
        this._areColumnDefinitionsStale.set(true);
    }

    private _baseColumnDefinitions = signal<ReportColumnDefinition[]>([]);
    /** Base column definitions for the active report.
     *  Derived from the report definition, column map, and hierarchy node levels and serves as the foundation for computing `activeColumnDefinitions`.
     */
    readonly baseColumnDefinitions = this._baseColumnDefinitions.asReadonly();

    private _activeColumnDefinitions = signal<ReportColumnDefinition[]>([]);
    /** Active column definitions for the current column layout.
     *  Reflects the base column definitions combined with the selected column layout.
     */
    readonly activeColumnDefinitions = this._activeColumnDefinitions.asReadonly();

    private _areColumnDefinitionsStale = signal<boolean>(false);
    /**
     * Indicates whether the current column definitions are out of date and require recomputation before the next report execution.
     *
     * This becomes true when structural change (e.g., subscriber switch).
     */
    readonly areColumnDefinitionsStale = this._areColumnDefinitionsStale.asReadonly();
    /** Marks the column definitions as stale. Recompute occurs during the next Report Execution */
    markColumnDefinitionsStale() {
        this._areColumnDefinitionsStale.set(true);
    }

    private _columnMap = signal<ReportColumnMap | null>(null);
    /** Server provided column key map for the active report.
     *
     * Maps logical column keys (e.g., `customerName`) to compact numeric property identifiers in report data.
     */
    readonly columnMap = this._columnMap.asReadonly();

    private _queryColumnDefaults = signal<Record<string, ColumnQueryState> | null>(null);
    /** Column state defaults parsed from query parameters (`c_` prefix). */
    readonly queryColumnDefaults = this._queryColumnDefaults.asReadonly();

    private _columnGroupDefinitions = signal<ReportColumnGroupDefinition[]>([]);
    /** Column group definitions for the active report. */
    readonly columnGroupDefinitions = this._columnGroupDefinitions.asReadonly();

    private _hierarchyNodeLevels = signal<HierarchyNodeLevelResponseDto[]>([]);
    /** Hierarchy node levels available for the current subscriber context. */
    readonly hierarchyNodeLevels = this._hierarchyNodeLevels.asReadonly();
    private updateHierarchyNodeLevels(subscriberKey: number | null) {
        let hierarchyNodeLevels$;
        if (subscriberKey == null) {
            hierarchyNodeLevels$ = of([]);
        } else {
            hierarchyNodeLevels$ = this.hierarchyNodeLevelService.list(subscriberKey);
        }

        return hierarchyNodeLevels$.pipe(
            tap((hierarchyNodeLevels) => {
                this._hierarchyNodeLevels.set(hierarchyNodeLevels);

                this.recomputeColumnDefinitions();
            })
        );
    }

    /**
     * Recomputes base and active column definitions for the current report context.
     *
     * Base column definitions always reflect the report's column definition.
     * Active column definitions reflect the currently selected layout, falling back to the report column definition if none is selected.
     *
     * Called during initialization and whenever the subscriber or hierarchy node levels change.
     * */
    private recomputeColumnDefinitions() {
        const reportColumnDefinition = this.reportDefinition()?.reportColumnDefinition;
        const columnMap = this.columnMap();
        const hierarchyNodeLevels = this.hierarchyNodeLevels();
        const includeAllColumns = this.includeAllColumns();

        if (!reportColumnDefinition || !columnMap) {
            return;
        }

        const activeLayoutKey = this.activeLayoutKey();
        const activeLayout = this.layouts().find((l) => l.itemKey === activeLayoutKey) ?? null;

        const baseColumnDefinitions = this.columnService.getColumnDefinitions(reportColumnDefinition, null, columnMap, hierarchyNodeLevels, includeAllColumns);
        this._baseColumnDefinitions.set(baseColumnDefinitions);

        const activeColumnDefinitions = this.columnService.getColumnDefinitions(reportColumnDefinition, activeLayout, columnMap, hierarchyNodeLevels, includeAllColumns);
        this._activeColumnDefinitions.set(activeColumnDefinitions);
    }

    getColumnDefinitionsFromLayoutDefinition(layoutDefinition: LayoutDefinition) {
        const reportColumnDefinition = this.reportDefinition()?.reportColumnDefinition;
        const columnMap = this.columnMap();
        const hierarchyNodeLevels = this.hierarchyNodeLevels();
        const includeAllColumns = this.includeAllColumns();

        if (!reportColumnDefinition || !columnMap) {
            return;
        }

        const columnDefinitions = this.columnService.getColumnDefinitionsFromLayoutDefinition(reportColumnDefinition, layoutDefinition, columnMap, hierarchyNodeLevels, includeAllColumns);

        return columnDefinitions;
    }

    // #endregion columns

    // #region ParameterSets
    /******************************************/
    /********** ParameterSets *****************/
    /******************************************/
    private _parameterSetCategories = signal<ReportCategory[]>([]);
    readonly parameterSetCategories = this._parameterSetCategories.asReadonly();

    private _parameterSets = signal<ReportItemEntity<ParameterSetDefinition>[]>([]);
    readonly parameterSets = this._parameterSets.asReadonly();

    private _activeParameterSetKey = signal<number | null>(null);
    readonly activeParameterSetKey = this._activeParameterSetKey.asReadonly();

    createParameterSetCategory(name: string, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null) {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return;
        }

        this.savedItemCategoryService
            .create(reportKey, ReportItemTypeKey.Parameter, name, scopeKey, subscriberKey, userId)
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    updateParameterSetCategory(parameterSetCategory: ReportCategory) {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return;
        }

        this.savedItemCategoryService
            .update(reportKey, parameterSetCategory)
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    deleteParameterSetCategory(categoryKey: number | null) {
        const reportKey = this.reportKey();
        if (reportKey == null || categoryKey == null) {
            return;
        }

        this.savedItemCategoryService
            .delete(reportKey, categoryKey, this.subscriberKey())
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    setActiveParameterSet(parameterSet: ReportItemEntity<ParameterSetDefinition> | null) {
        if (parameterSet == null) {
            this._activeParameterSetKey.set(null);
            return;
        }

        this._activeParameterSetKey.set(parameterSet.itemKey);

        /* Get Parameter Definitions */
        const reportDefinition = this.reportDefinition();

        if (!reportDefinition) {
            return;
        }

        const parameterDefinitions = this.parametersService.getParameterDefinitions(reportDefinition);
        this._parameterDefinitions.set(parameterDefinitions);

        /* Get Values from the saved set */
        const parameterValues = parameterSet.itemDefinition ?? {};

        const allDefs = parameterDefinitions.flatMap((d) => [d, ...(d.additionalParameters ?? [])]);

        allDefs.forEach((def) => {
            const savedValue = def.key in parameterValues ? parameterValues[def.key] : def.defaultValue;
            const parameterValue = def.valueType === 'date' ? this.parseSavedDateParameterValue(savedValue) : savedValue;

            this.setParam(def.key, parameterValue);
        });

        this.refreshParameters();
    }

    private getParameterSetDefinition(): ParameterSetDefinition {
        const parameterValues = this.parameterValues();
        const definition: ParameterSetDefinition = { ...parameterValues };

        /* Persist dates as DateOnly strings so a saved date always reads back as the same calendar date, regardless of the viewer's time zone. */
        for (const key of this.getDateParameterKeys()) {
            const value = definition[key];

            if (value instanceof Date) {
                definition[key] = toDateOnlyString(value);
            }
        }

        return definition;
    }

    /** Keys of every parameter, including additional parameters, whose value is a date. */
    private getDateParameterKeys(): string[] {
        const allDefs = this.parameterDefinitions().flatMap((d) => [d, ...(d.additionalParameters ?? [])]);

        return allDefs.filter((def) => def.valueType === 'date').map((def) => def.key);
    }

    /**
     * Converts a persisted date parameter value back into a Date at local midnight.
     *
     * Saved definitions store DateOnly strings. Parameter sets saved before that change hold a full
     * UTC timestamp, so those are parsed as an instant and reduced to the calendar date they were saved as.
     */
    private parseSavedDateParameterValue(savedValue: unknown): Date | null {
        if (savedValue instanceof Date) {
            return savedValue;
        }

        if (typeof savedValue !== 'string' || savedValue.trim() === '') {
            return null;
        }

        const dateOnly = fromDateOnlyString(savedValue);

        if (dateOnly != null) {
            return dateOnly;
        }

        const legacyTimestamp = new Date(savedValue);

        return fromDateOnlyString(toDateOnlyString(legacyTimestamp));
    }

    createParameterSet(name: string, categoryKey: number | null, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        const definition = this.getParameterSetDefinition();

        const newParameterSet: ReportItemCreateRequestDto<ParameterSetDefinition> = {
            itemName: name,
            categoryKey: categoryKey,
            reportItemTypeKey: ReportItemTypeKey.Parameter,
            reportItemScopeKey: scopeKey,
            subscriberKey: subscriberKey,
            userId: userId,
            itemDefinition: definition
        };

        return this.reportItemService.create(reportKey, newParameterSet).pipe(
            tap((itemKey) => {
                if (scopeKey !== ReportItemScopeKey.Subscriber || subscriberKey === this.subscriberKey()) {
                    const parameterSet = this.createLocalReportItem(reportKey, itemKey, newParameterSet);
                    this._reportItems.update((items) => [...items, parameterSet]);
                    this._parameterSets.update((parameterSets) => [...parameterSets, parameterSet]);
                    /* Select the new Parameter Set */
                    this.setActiveParameterSet(parameterSet);
                }
            })
        );
    }

    updateParameterSetDefinition(parameterSetKey: number): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        const definition = this.getParameterSetDefinition();

        return this.reportItemService.updateDefinition(reportKey, parameterSetKey, this.subscriberKey(), definition).pipe(
            tap(() => {
                this._reportItems.update((items) => items.map((item) => (item.itemKey === parameterSetKey ? { ...item, itemDefinition: definition } : item)));
                this._parameterSets.update((parameterSets) => parameterSets.map((f) => (f.itemKey === parameterSetKey ? { ...f, itemDefinition: definition } : f)));
            })
        );
    }

    updateParameterSetMetaData(parameterSetKey: number, name: string, categoryKey: number | null, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        return this.reportItemService
            .updateMetadata(reportKey, parameterSetKey, this.subscriberKey(), {
                itemName: name,
                reportItemScopeKey: scopeKey,
                subscriberKey,
                userId,
                categoryKey
            })
            .pipe(
                tap(() => {
                    if (scopeKey === ReportItemScopeKey.Subscriber && subscriberKey != this.subscriberKey()) {
                        this._reportItems.update((items) => items.filter((p) => p.itemKey !== parameterSetKey));
                        this._parameterSets.update((parameterSets) => parameterSets.filter((p) => p.itemKey !== parameterSetKey));

                        if (parameterSetKey === this.activeParameterSetKey()) {
                            this.setActiveParameterSet(null);
                        }
                    } else {
                        this._reportItems.update((items) => items.map((p) => (p.itemKey === parameterSetKey ? { ...p, itemName: name, categoryKey, reportItemScopeKey: scopeKey, subscriberKey, userId } : p)));
                        this._parameterSets.update((parameterSets) => parameterSets.map((p) => (p.itemKey === parameterSetKey ? { ...p, itemName: name, categoryKey, reportItemScopeKey: scopeKey, subscriberKey, userId } : p)));
                    }
                })
            );
    }

    deleteParameterSet(parameterSetKey: number): Observable<void | false> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(false);
        }

        return this.reportItemService.delete(reportKey, parameterSetKey, this.subscriberKey()).pipe(
            tap(() => {
                this._reportItems.update((items) => items.filter((p) => p.itemKey !== parameterSetKey));
                this._parameterSets.update((parameterSets) => parameterSets.filter((p) => p.itemKey !== parameterSetKey));

                if (parameterSetKey === this.activeParameterSetKey()) {
                    this.setActiveParameterSet(null);
                }
            })
        );
    }

    shareParameterSet(parameterSetKey: number, recipientUserId: string): Observable<number | false> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(false);
        }

        return this.reportItemService.share(reportKey, parameterSetKey, this.subscriberKey(), recipientUserId);
    }
    // #endregion Parameter Sets

    // #region Layouts
    /******************************************/
    /************ Layouts *********************/
    /******************************************/
    private _layoutCategories = signal<ReportCategory[]>([]);
    /** All layout categories available for this report. */
    readonly layoutCategories = this._layoutCategories.asReadonly();

    private _reportItems = signal<ReportItemEntity[]>([]);

    private _layouts = signal<ReportItemEntity<LayoutDefinition>[]>([]);
    /** All layouts available for this report. */
    readonly layouts = this._layouts.asReadonly();

    private _activeLayoutKey = signal<number | null>(null);
    /** The currently active column layout key for this report. */
    readonly activeLayoutKey = this._activeLayoutKey.asReadonly();

    activateBaseLayout() {
        this.setActiveLayout(null);
    }

    readonly layoutOptions = computed(() => {
        const defaultLayoutOptions = this.reportDefinition()?.defaultLayoutOptions ?? {};
        const reportColumnDefinition = this.reportDefinition()?.reportColumnDefinition;
        const activeLayoutKey = this.activeLayoutKey();

        if (!reportColumnDefinition) {
            return null;
        }

        const activeLayoutOptions = this.layouts().find((l) => l.itemKey === activeLayoutKey)?.itemDefinition?.layoutOptions ?? {};

        return {
            ...defaultLayoutOptions,
            ...activeLayoutOptions
        };
    });

    /**
     * Sets the active layout for the report and recomputes column definitions.
     *
     * This is the single entry point for changing the active layout.
     * Updates both the active layout key and the active column definitions.
     *
     * Falls back to the report column definition if the Provided key does not match an available layout.
     */
    setActiveLayout(activeLayoutKey: number | null) {
        if (activeLayoutKey === this.activeLayoutKey()) {
            this.refreshLayout();
        }

        const reportColumnDefinition = this._reportDefinition()?.reportColumnDefinition;
        const columnMap = this._columnMap();
        const hierarchyNodeLevels = this.hierarchyNodeLevels() ?? [];
        const includeAllColumns = this.includeAllColumns();

        if (!reportColumnDefinition || !columnMap) {
            return;
        }

        const activeLayout = this.layouts().find((l) => l.itemKey === activeLayoutKey) ?? null;

        const activeColumnDefinitions = this.columnService.getColumnDefinitions(reportColumnDefinition, activeLayout, columnMap, hierarchyNodeLevels, includeAllColumns);
        this._activeColumnDefinitions.set(activeColumnDefinitions);
        this._activeLayoutKey.set(activeLayout?.itemKey ?? null);
    }

    createLayoutCategory(name: string, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null) {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return;
        }

        this.savedItemCategoryService
            .create(reportKey, ReportItemTypeKey.Layout, name, scopeKey, subscriberKey, userId)
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    updateLayoutCategory(layoutCategory: ReportCategory) {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return;
        }

        this.savedItemCategoryService
            .update(reportKey, layoutCategory)
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    deleteLayoutCategory(categoryKey: number | null) {
        const reportKey = this.reportKey();
        if (reportKey == null || categoryKey == null) {
            return;
        }

        this.savedItemCategoryService
            .delete(reportKey, categoryKey, this.subscriberKey())
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    private _layoutDefinitionProvider = signal<(() => LayoutDefinition) | null>(null);

    readonly hasLayoutDefinitionProvider = computed(() => this._layoutDefinitionProvider() !== null);
    /** Registers the function to convert the current Grid or Report state to a Layout Definition when a layout is saved. */
    registerLayoutDefinitionProvider(fn: () => LayoutDefinition) {
        this._layoutDefinitionProvider.set(fn);
    }

    createLayout(name: string, layoutCategoryKey: number | null, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        const definition: LayoutDefinition = this.hasLayoutDefinitionProvider() ? this._layoutDefinitionProvider()!() : { columns: [] };

        const newLayout: ReportItemCreateRequestDto<LayoutDefinition> = {
            itemName: name,
            categoryKey: layoutCategoryKey,
            reportItemTypeKey: ReportItemTypeKey.Layout,
            reportItemScopeKey: scopeKey,
            subscriberKey: subscriberKey,
            userId: userId,
            itemDefinition: definition
        };

        return this.reportItemService.create(reportKey, newLayout).pipe(
            tap((itemKey) => {
                if (scopeKey !== ReportItemScopeKey.Subscriber || subscriberKey === this.subscriberKey()) {
                    const layout = this.createLocalReportItem(reportKey, itemKey, newLayout);
                    this._reportItems.update((items) => [...items, layout]);
                    this._layouts.update((layouts) => [...layouts, layout]);
                    /* Select the new layout */
                    this.setActiveLayout(itemKey);
                }
            })
        );
    }

    updateLayoutDefinition(layoutKey: number): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        const definition: LayoutDefinition = this.hasLayoutDefinitionProvider() ? this._layoutDefinitionProvider()!() : { columns: [] };

        return this.reportItemService.updateDefinition(reportKey, layoutKey, this.subscriberKey(), definition).pipe(
            tap(() => {
                this._reportItems.update((items) => items.map((l) => (l.itemKey === layoutKey ? { ...l, itemDefinition: definition } : l)));
                this._layouts.update((layouts) => layouts.map((l) => (l.itemKey === layoutKey ? { ...l, itemDefinition: definition } : l)));
            })
        );
    }

    updateLayoutMetaData(layoutKey: number, name: string, layoutCategoryKey: number | null, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        return this.reportItemService
            .updateMetadata(reportKey, layoutKey, this.subscriberKey(), {
                itemName: name,
                reportItemScopeKey: scopeKey,
                subscriberKey,
                userId,
                categoryKey: layoutCategoryKey
            })
            .pipe(
                tap(() => {
                    if (scopeKey === ReportItemScopeKey.Subscriber && subscriberKey != this.subscriberKey()) {
                        this._reportItems.update((items) => items.filter((l) => l.itemKey !== layoutKey));
                        this._layouts.update((layouts) => layouts.filter((l) => l.itemKey !== layoutKey));

                        if (layoutKey === this.activeLayoutKey()) {
                            this.activateBaseLayout();
                        }
                    } else {
                        this._reportItems.update((items) =>
                            items.map((l) =>
                                l.itemKey === layoutKey
                                    ? {
                                          ...l,
                                          itemName: name,
                                          categoryKey: layoutCategoryKey,
                                          reportItemScopeKey: scopeKey,
                                          subscriberKey: subscriberKey,
                                          userId: userId
                                      }
                                    : l
                            )
                        );
                        this._layouts.update((layouts) =>
                            layouts.map((l) =>
                                l.itemKey === layoutKey
                                    ? {
                                          ...l,
                                          itemName: name,
                                          categoryKey: layoutCategoryKey,
                                          reportItemScopeKey: scopeKey,
                                          subscriberKey: subscriberKey,
                                          userId: userId
                                      }
                                    : l
                            )
                        );
                    }
                })
            );
    }

    deleteLayout(layoutKey: number): Observable<void | false> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(false);
        }

        return this.reportItemService.delete(reportKey, layoutKey, this.subscriberKey()).pipe(
            tap(() => {
                this._reportItems.update((items) => items.filter((l) => l.itemKey !== layoutKey));
                this._layouts.update((layouts) => layouts.filter((l) => l.itemKey !== layoutKey));

                if (layoutKey === this.activeLayoutKey()) {
                    this.activateBaseLayout();
                }
            })
        );
    }

    shareLayout(layoutKey: number, recipientUserId: string): Observable<number | false> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(false);
        }

        return this.reportItemService.share(reportKey, layoutKey, this.subscriberKey(), recipientUserId);
    }

    // #endregion layouts

    // #region FilterSets
    /******************************************/
    /************* FilterSets *****************/
    /******************************************/
    private _filterSetCategories = signal<ReportCategory[]>([]);
    readonly filterSetCategories = this._filterSetCategories.asReadonly();

    private _filterSets = signal<ReportItemEntity<FilterSetDefinition>[]>([]);
    readonly filterSets = this._filterSets.asReadonly();

    private _activeFilterSetKey = signal<number | null>(null);
    readonly activeFilterSetKey = this._activeFilterSetKey.asReadonly();

    createFilterSetCategory(name: string, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null) {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return;
        }

        this.savedItemCategoryService
            .create(reportKey, ReportItemTypeKey.Filter, name, scopeKey, subscriberKey, userId)
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    updateFilterSetCategory(filterSetCategory: ReportCategory) {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return;
        }

        this.savedItemCategoryService
            .update(reportKey, filterSetCategory)
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    deleteFilterSetCategory(categoryKey: number | null) {
        const reportKey = this.reportKey();
        if (reportKey == null || categoryKey == null) {
            return;
        }

        this.savedItemCategoryService
            .delete(reportKey, categoryKey, this.subscriberKey())
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    setActiveFilterSet(filterSet: ReportItemEntity<FilterSetDefinition> | null) {
        if (filterSet) {
            if (filterSet.itemKey === this.activeFilterSetKey()) {
                this.refreshFilters();
            }
        }

        this._activeFilterSetKey.set(filterSet?.itemKey ?? null);

        this.setSearchTerm(filterSet?.itemDefinition?.searchTerm ?? '');
    }

    private _filterSetModelDefinitionProvider = signal<(() => unknown) | null>(null);

    readonly hasFilterSetModelDefinitionProvider = computed(() => this._filterSetModelDefinitionProvider() !== null);
    /** Registers the function to convert the current Filter state to a Filter Definition when a filter set is saved. */
    registerFilterSetDefinitionProvider(fn: () => unknown) {
        this._filterSetModelDefinitionProvider.set(fn);
    }

    private getFilterSetDefinition(): FilterSetDefinition {
        const searchTerm = this.searchTerm();
        const filterModel: unknown = this.hasFilterSetModelDefinitionProvider() ? this._filterSetModelDefinitionProvider()!() : {};

        return {
            searchTerm: searchTerm,
            filterModel: filterModel
        };
    }

    createFilterSet(name: string, categoryKey: number | null, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        const filterSetDefinition = this.getFilterSetDefinition();

        const newFilterSet: ReportItemCreateRequestDto<FilterSetDefinition> = {
            itemName: name,
            categoryKey: categoryKey,
            reportItemTypeKey: ReportItemTypeKey.Filter,
            reportItemScopeKey: scopeKey,
            subscriberKey: subscriberKey,
            userId: userId,
            itemDefinition: filterSetDefinition
        };

        return this.reportItemService.create(reportKey, newFilterSet).pipe(
            tap((itemKey) => {
                if (scopeKey !== ReportItemScopeKey.Subscriber || subscriberKey === this.subscriberKey()) {
                    const filterSet = this.createLocalReportItem(reportKey, itemKey, newFilterSet);
                    this._reportItems.update((items) => [...items, filterSet]);
                    this._filterSets.update((filterSets) => [...filterSets, filterSet]);
                    /* Select the new Filter Set */
                    this.setActiveFilterSet(filterSet);
                }
            })
        );
    }

    updateFilterSetDefinition(filterSetKey: number): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        const filterSetDefinition = this.getFilterSetDefinition();

        return this.reportItemService.updateDefinition(reportKey, filterSetKey, this.subscriberKey(), filterSetDefinition).pipe(
            tap(() => {
                this._reportItems.update((items) => items.map((f) => (f.itemKey === filterSetKey ? { ...f, itemDefinition: filterSetDefinition } : f)));
                this._filterSets.update((filterSets) => filterSets.map((f) => (f.itemKey === filterSetKey ? { ...f, itemDefinition: filterSetDefinition } : f)));
            })
        );
    }

    updateFilterSetMetaData(filterSetKey: number, name: string, categoryKey: number | null, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        return this.reportItemService
            .updateMetadata(reportKey, filterSetKey, this.subscriberKey(), {
                itemName: name,
                reportItemScopeKey: scopeKey,
                subscriberKey,
                userId,
                categoryKey
            })
            .pipe(
                tap(() => {
                    if (scopeKey === ReportItemScopeKey.Subscriber && subscriberKey != this.subscriberKey()) {
                        this._reportItems.update((items) => items.filter((f) => f.itemKey !== filterSetKey));
                        this._filterSets.update((filterSets) => filterSets.filter((f) => f.itemKey !== filterSetKey));

                        if (filterSetKey === this.activeFilterSetKey()) {
                            this.setActiveFilterSet(null);
                        }
                    } else {
                        this._reportItems.update((items) => items.map((f) => (f.itemKey === filterSetKey ? { ...f, itemName: name, categoryKey, reportItemScopeKey: scopeKey, subscriberKey, userId } : f)));
                        this._filterSets.update((filterSets) => filterSets.map((f) => (f.itemKey === filterSetKey ? { ...f, itemName: name, categoryKey, reportItemScopeKey: scopeKey, subscriberKey, userId } : f)));
                    }
                })
            );
    }

    deleteFilterSet(filterSetKey: number): Observable<void | false> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(false);
        }

        return this.reportItemService.delete(reportKey, filterSetKey, this.subscriberKey()).pipe(
            tap(() => {
                this._reportItems.update((items) => items.filter((f) => f.itemKey !== filterSetKey));
                this._filterSets.update((filterSets) => filterSets.filter((f) => f.itemKey !== filterSetKey));

                if (filterSetKey === this.activeFilterSetKey()) {
                    this.setActiveFilterSet(null);
                }
            })
        );
    }

    shareFilterSet(filterSetKey: number, recipientUserId: string): Observable<number | false> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(false);
        }

        return this.reportItemService.share(reportKey, filterSetKey, this.subscriberKey(), recipientUserId);
    }
    // #endregion filterSets

    // #region ReportViews
    /******************************************/
    /************* ReportViews *****************/
    /******************************************/
    private _reportViewCategories = signal<ReportCategory[]>([]);
    readonly reportViewCategories = this._reportViewCategories.asReadonly();

    private _reportViews = signal<ReportViewEntity[]>([]);
    readonly reportViews = this._reportViews.asReadonly();

    private _activeReportViewKey = signal<number | null>(null);
    readonly activeReportViewKey = this._activeReportViewKey.asReadonly();

    createReportViewCategory(name: string, scopeKey: ReportItemScopeKey, subscriberKey: number | null, userId: string | null) {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return;
        }

        this.savedItemCategoryService
            .create(reportKey, ReportItemTypeKey.View, name, scopeKey, subscriberKey, userId)
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    updateReportViewCategory(reportViewCategory: ReportCategory) {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return;
        }

        this.savedItemCategoryService
            .update(reportKey, reportViewCategory)
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    deleteReportViewCategory(categoryKey: number | null) {
        const reportKey = this.reportKey();
        if (reportKey == null || categoryKey == null) {
            return;
        }

        this.savedItemCategoryService
            .delete(reportKey, categoryKey, this.subscriberKey())
            .pipe(switchMap(() => this.reloadCategories()))
            .subscribe();
    }

    setActiveReportView(reportView: ReportViewEntity | null, applySelections = true) {
        this._activeReportViewKey.set(reportView?.viewKey ?? null);

        if (!applySelections || reportView == null) {
            return;
        }

        if (reportView.layoutKey && this.layouts().some((l) => l.itemKey === reportView.layoutKey)) {
            this.setActiveLayout(reportView.layoutKey);
        } else {
            this.activateBaseLayout();
        }

        const parameterSet = reportView.parameterSetKey ? (this._parameterSets().find((p) => p.itemKey === reportView.parameterSetKey) ?? null) : null;

        if (parameterSet) {
            this.setActiveParameterSet(parameterSet);
        } else {
            this.resetParameters();
            this._activeReportViewKey.set(reportView.viewKey ?? null);
        }

        const filterSet = reportView.filterSetKey ? (this._filterSets().find((f) => f.itemKey === reportView.filterSetKey) ?? null) : null;

        if (filterSet) {
            this.setActiveFilterSet(filterSet);
        } else {
            this.clearFilters();
        }
    }

    clearActiveReportView() {
        this._activeReportViewKey.set(null);
    }

    createReportView(
        name: string,
        categoryKey: number | null,
        scopeKey: ReportItemScopeKey,
        subscriberKey: number | null,
        userId: string | null,
        layoutKey: number | null,
        parameterSetKey: number | null,
        filterSetKey: number | null
    ): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        const newView: ReportViewUpsertRequestDto = {
            viewName: name,
            categoryKey,
            reportItemScopeKey: scopeKey,
            subscriberKey,
            userId,
            layoutKey,
            parameterSetKey,
            filterSetKey
        };

        return this.reportViewService.create(reportKey, newView).pipe(
            tap((viewKey) => {
                if (scopeKey !== ReportItemScopeKey.Subscriber || subscriberKey === this.subscriberKey()) {
                    const reportView: ReportViewEntity = {
                        viewKey,
                        reportKey,
                        ...newView,
                        rowStatusKey: 1,
                        insertUser: '',
                        insertDate: new Date(),
                        updateUser: '',
                        updateDate: ''
                    };

                    this._reportViews.update((views) => [...views, reportView]);
                }
            })
        );
    }

    updateReportView(
        viewKey: number,
        name: string,
        categoryKey: number | null,
        scopeKey: ReportItemScopeKey,
        subscriberKey: number | null,
        userId: string | null,
        layoutKey: number | null,
        parameterSetKey: number | null,
        filterSetKey: number | null
    ): Observable<number | null> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(null);
        }

        const reportView: ReportViewUpsertRequestDto = {
            viewName: name,
            reportItemScopeKey: scopeKey,
            subscriberKey,
            userId,
            categoryKey,
            layoutKey,
            parameterSetKey,
            filterSetKey
        };

        return this.reportViewService.update(reportKey, viewKey, reportView).pipe(
            tap(() => {
                if (scopeKey === ReportItemScopeKey.Subscriber && subscriberKey != this.subscriberKey()) {
                    this._reportViews.update((views) => views.filter((v) => v.viewKey !== viewKey));
                } else {
                    this._reportViews.update((views) => views.map((v) => (v.viewKey === viewKey ? { ...v, ...reportView } : v)));
                }
            })
        );
    }

    deleteReportView(viewKey: number): Observable<void | false> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(false);
        }

        return this.reportViewService.delete(reportKey, viewKey, this.subscriberKey()).pipe(
            tap(() => {
                this._reportViews.update((views) => views.filter((v) => v.viewKey !== viewKey));

                if (viewKey === this.activeReportViewKey()) {
                    this.setActiveReportView(null, false);
                }
            })
        );
    }

    shareReportView(viewKey: number, recipientUserId: string): Observable<number | false> {
        const reportKey = this.reportKey();
        if (reportKey == null) {
            return of(false);
        }

        return this.reportViewService.share(reportKey, viewKey, this.subscriberKey(), recipientUserId);
    }
    // #endregion Report Views

    // #region execution
    /*************************************/
    /************** Execution ************/
    /*************************************/
    private _isReportRunning = signal(false);
    /** Indicates whether the report is currently executing. */
    readonly isReportRunning = this._isReportRunning.asReadonly();
    private reportExecutionSubscription: Subscription | null = null;

    private _reportData = signal<any[]>([]);
    /** Current report result rows. */
    readonly reportData = this._reportData.asReadonly();

    /** Callback registered by GridComponent to handle stale column definitions. */
    private _columnDefinitionsStaleHandler: (() => void) | null = null;
    /** Registers a callback invoked when column definitions become stale and need to be refreshed.  */
    registerColumnDefinitionsStaleHandler(fn: () => void) {
        this._columnDefinitionsStaleHandler = fn;
    }

    /**
     * Executes the active report using the current parameter values.
     *
     * Delegates data retrieval to the report definition's `fetchData` function.
     */
    executeReport = () => {
        this.cancelReport();

        if (!this.areParametersValid()) {
            return;
        }
        const fetchData = this.reportDefinition()?.fetchData;

        if (!fetchData) {
            throw Error('Report Definition is missing a Fetch Rows method.');
        }

        /* Callback to update the Data Component when the Column Definitions have been modified */
        if (this._areColumnDefinitionsStale()) {
            if (this._columnDefinitionsStaleHandler) {
                this._columnDefinitionsStaleHandler();
            }

            this._areColumnDefinitionsStale.set(false);
        }

        this._isReportRunning.set(true);
        this.reportExecutionSubscription = fetchData(this.httpClient, this.subscriberKey(), this.parameterValues())
            .pipe(
                finalize(() => {
                    this._isReportRunning.set(false);
                    this.reportExecutionSubscription = null;
                })
            )
            .subscribe((reportResponse) => {
                this._reportData.set(reportResponse.data);
                this._columnMap.set(reportResponse.columnMap);
            });
    };

    /** Cancels the current report execution, if one is in progress. */
    cancelReport = () => {
        this.reportExecutionSubscription?.unsubscribe();
    };

    /**
     * Fetches the current user's API key and builds a fully qualified integration URL for the active report.
     * Requires the report definition to have both integrationEndpoint and buildIntegrationQueryParams set.
     */
    buildIntegrationUrl(): Observable<string> {
        return this.userService.getUserApiKey().pipe(
            map((apiKey) => {
                const def = this.reportDefinition();
                const subscriberKey = this.subscriberKey();

                if (!def?.integrationEndpoint || !def?.buildIntegrationQueryParams || !subscriberKey) {
                    throw new Error('Cannot build integration URL: report definition or subscriber key is missing.');
                }

                const queryParams = def.buildIntegrationQueryParams(this.parameterValues());
                const searchParams = new URLSearchParams();

                for (const [key, value] of Object.entries(queryParams)) {
                    if (Array.isArray(value)) {
                        for (const v of value) searchParams.append(key, v);
                    } else {
                        searchParams.set(key, value);
                    }
                }

                searchParams.set('apiKey', apiKey);

                const base = `${this.globalConfigService.apiUrl}/api/subscriber/${subscriberKey}/integrations/${def.integrationEndpoint}`;
                return `${base}?${searchParams.toString()}`;
            })
        );
    }

    // #endregion execution

    // #region interaction triggers
    /*************************************/
    /******** Interaction Triggers *******/
    /*************************************/
    /*
     * All signals in this section represent onetime interaction requests.
     * Consumers must observe and handle them.
     */

    private _autoRunRequest = signal<number>(0);
    /** Signal that requests the form auto run once parameters are loaded and valid. */
    readonly autoRunRequest = this._autoRunRequest.asReadonly();
    /** Requests a one-time automatic report execution. */
    autoRun() {
        this._autoRunRequest.update((n) => n + 1);
    }
    /** Clears any pending auto-run request. */
    clearAutoRunRequest() {
        this._autoRunRequest.set(0);
    }

    private _parametersResetRequest = signal<number>(0);
    /** Signal that notifies consumers (e.g. the parameters form) that parameters have been reset and the form should sync. */
    readonly parametersResetRequest = this._parametersResetRequest.asReadonly();

    private _parametersRefreshRequest = signal<number>(0);
    /** Signal that requests refreshing all parameter components to the current value in the workspace. */
    readonly parametersRefreshRequest = this._parametersRefreshRequest.asReadonly();
    /** Requests refreshing all parameters to use the current value in the workspace. */
    refreshParameters() {
        this._parametersRefreshRequest.update((n) => n + 1);
    }

    private _clearFiltersRequest = signal<number>(0);
    /** Signal that requests clearing all filters in the current report view. */
    readonly clearFiltersRequest = this._clearFiltersRequest.asReadonly();
    /** Requests clearing all active filters in the current report view. */
    clearFilters() {
        /* Deselect the Active Filter Set */
        this.setActiveFilterSet(null);
        this.setSearchTerm('');
        this._clearFiltersRequest.update((n) => n + 1);
    }

    private _refreshFiltersRequest = signal<number>(0);
    /** Signal that requests refreshing all filter components to the currently selected Filter Set */
    readonly refreshFiltersRequest = this._refreshFiltersRequest.asReadonly();
    refreshFilters() {
        this._refreshFiltersRequest.update((n) => n + 1);
    }

    private _refreshLayoutRequest = signal<number>(0);
    /** Signal that requests refreshing the layout to the currently selected Layout */
    readonly refreshLayoutRequest = this._refreshLayoutRequest.asReadonly();
    refreshLayout() {
        this._refreshLayoutRequest.update((n) => n + 1);
    }

    /**
     * Clears the report data and reapplies the currently selected layout.
     *
     * Use this when structural column changes (e.g., subscriber switch, include-all-columns toggle) have made
     * the grid's column order inconsistent with the selected layout. New columns will appear in their
     * layout-defined positions rather than appended to the end.
     */
    resetGrid() {
        this._reportData.set([]);
        this.setActiveLayout(this._activeLayoutKey());
        this._areColumnDefinitionsStale.set(false);
    }

    private _expandAllRequest = signal<number>(0);
    /** Signal that requests expanding all items in the current report view. */
    readonly expandAllRequest = this._expandAllRequest.asReadonly();
    /** Requests expanding all expandable rows or groups in the current report view. */
    expandAll() {
        this._expandAllRequest.update((n) => n + 1);
    }

    private _collapseAllRequest = signal<number>(0);
    /** Signal that requests collapsing all items in the current report view. */
    readonly collapseAllRequest = this._collapseAllRequest.asReadonly();
    /** Requests collapsing all expandable rows or groups in the current report view. */
    collapseAll() {
        this._collapseAllRequest.update((n) => n + 1);
    }

    private _autosizeColumnsRequest = signal<number>(0);
    /** Signal that requests auto resizing all columns in the current report view. */
    readonly autosizeColumnsRequest = this._autosizeColumnsRequest.asReadonly();
    autosizeColumns() {
        this._autosizeColumnsRequest.update((n) => n + 1);
    }

    private _exportRequest = signal<ExportOptions | null>(null);
    /**
     * Current export request format for the report (CSV or EXCEL).
     *
     * null indicates no active request and the handler must reset it to null after processing.
     */
    readonly exportRequest = this._exportRequest.asReadonly();
    /** Sets the current export request for the report.
     *
     * - `'CSV'` or `'EXCEL'` triggers an export action.
     * - `null` clears any pending request.
     */
    setExportRequest(request: ExportOptions | null) {
        this._exportRequest.set(request);
    }

    // #endregion interaction trigger

    // #region view state
    /*************************************/
    /************* View State ************/
    /*************************************/
    private _searchTerm = signal<string>('');
    /** Current search term entered at the top of the report page. */
    readonly searchTerm = this._searchTerm.asReadonly();
    /** Updates the current global search term. */
    setSearchTerm(searchTerm: string) {
        this._searchTerm.set(searchTerm);
    }

    private _areReportFiltersActive = signal<boolean>(false);
    /**
     * Indicates whether any filters are currently active within the report view (grid or custom component).
     *
     * This is scoped to the report context and does not represent global application filters.
     */
    readonly areReportFiltersActive = this._areReportFiltersActive.asReadonly();
    /** Updates whether any filters are currently active in the report view. */
    setReportFiltersActive(isActive: boolean) {
        this._areReportFiltersActive.set(isActive);
    }

    /** Indicates if any "Filter" related item is Active. Including Grid Filters, Search Text, and an Active Filter Set. */
    readonly areAnyFiltersActive = computed(() => {
        const hasSearchTerm = this.searchTerm() != '';
        const areReportFiltersActive = this.areReportFiltersActive();
        const hasActiveFilterSet = this.activeFilterSetKey() != null;

        return hasSearchTerm || areReportFiltersActive || hasActiveFilterSet;
    });

    private readonly _isParametersFormSubmitted = signal<boolean>(false);
    /** Tracks whether the parameters form has been submitted. */
    isParametersFormSubmitted = this._isParametersFormSubmitted.asReadonly();
    /** Updates the submitted state of the parameters form. */
    setParametersFormSubmitted(isSubmitted: boolean) {
        this._isParametersFormSubmitted.set(isSubmitted);
    }

    private _isConfigurationPanelOpen = signal(false);
    /** Indicates whether the report configuration panel is currently open. */
    isConfigurationPanelOpen = this._isConfigurationPanelOpen.asReadonly();
    /** Toggles the configuration panel open/closed state. */
    toggleConfigurationPanel = () => this._isConfigurationPanelOpen.update((s) => !s);
    /** Opens the configuration panel. */
    openConfigurationPanel = () => this._isConfigurationPanelOpen.set(true);
    /** Closes the configuration panel. */
    closeConfigurationPanel = () => this._isConfigurationPanelOpen.set(false);

    // #endregion view state
}
